import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { useAchievements } from "../contexts/AchievementContext";
import apiService from "../services/api";
import ItemScanner from "../components/ItemScanner";
import HelpDisclosure from "../components/HelpDisclosure";
import ModalPortal from "../components/ModalPortal";
import { parseQuantityText, formatQuantityValue } from "../utils/pantryQuantity";
// import { isValidEuropeanDate } from "../utils/dateUtils";
import "../styles/Pantry.css";
import "../styles/DeleteConfirmation.css";

const pantryItemHelp = [
  {
    question: "What should I put in quantity?",
    answer: "Use any readable amount, such as 2, 500 g, 1 kg, 1 bottle, or 12 pieces. The pantry cards will show it exactly as entered."
  },
  {
    question: "Which location should I choose?",
    answer: "Choose where the food is stored: pantry, fridge, freezer, counter, or cabinet. This helps you find items later."
  },
  {
    question: "Do I need an expiry date?",
    answer: "No. Add it when you want reminders and use-soon warnings. Clear it for items that do not expire or when you are unsure."
  }
];

const Pantry = ({ sidebarOpen }) => {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useUserSettings();
  const { handleApiResponse, isShowingAchievement } = useAchievements();
  const [searchQuery, setSearchQuery] = useState("");
  const [pantryItems, setPantryItems] = useState([]);
  const [stats, setStats] = useState({
    totalItems: 0,
    expiringSoon: 0,
    expired: 0,
    fridgeItems: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    location: 'pantry',
    expiryDate: '',
    description: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteContext, setDeleteContext] = useState('manual');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showItemScanner, setShowItemScanner] = useState(false);
  const [showAddChoiceModal, setShowAddChoiceModal] = useState(false);
  const [showQuickIngredientAdd, setShowQuickIngredientAdd] = useState(false);
  const [isQuickStartFlow, setIsQuickStartFlow] = useState(false);
  const [selectedQuickIngredients, setSelectedQuickIngredients] = useState([]);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(false);
  const suggestionsRef = useRef([]);
  const isFetchingRef = useRef(false);
  const lastExpiringItemsHashRef = useRef('');
  const expiryTrackingDisabled = settings.disableExpiryTracking === true;

  const getFallbackExpiryDate = useCallback(() => {
    if (expiryTrackingDisabled) {
      return '2099-12-31';
    }
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  }, [expiryTrackingDisabled]);

  const getInitialExpiryDate = useCallback(() => (
    expiryTrackingDisabled ? getFallbackExpiryDate() : ''
  ), [expiryTrackingDisabled, getFallbackExpiryDate]);

  const quickStartKey = useMemo(() => {
    return `prep_right_quick_start_done_${user?.id || user?.email || 'user'}`;
  }, [user]);

  const onboardingReadyKey = useMemo(() => {
    return `prep_right_onboarding_ready_${user?.id || user?.email || 'user'}`;
  }, [user]);

  const onboardingDoneKey = useMemo(() => {
    return `prep_right_onboarding_done_${user?.id || user?.email || 'user'}`;
  }, [user]);

  // Clear AI suggestions cache when pantry is updated
  const clearAISuggestionsCache = useCallback(() => {
    lastExpiringItemsHashRef.current = '';
    setAiSuggestions([]);
    setSuggestionsError(false);
    suggestionsRef.current = [];
    isFetchingRef.current = false;
  }, []);

  const showSuccess = useCallback((message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccess(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  }, [isShowingAchievement]);

  const loadPantryData = useCallback(async (clearCache = false) => {
    setIsLoading(true);
    try {
      const [itemsResponse, statsResponse] = await Promise.all([
        apiService.getPantryItems(),
        apiService.getPantryStats()
      ]);
      
      setPantryItems(itemsResponse.items);
      setStats(statsResponse.stats);
      
      // Only clear cache if explicitly requested
      if (clearCache) {
        clearAISuggestionsCache();
      }
    } catch (error) {
      console.error('Failed to load pantry data:', error);
      showSuccess('Failed to load pantry data. Please try again.');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty to prevent infinite loops

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Calculate expiring items - memoized to prevent infinite loops
  const expiringSoon = useMemo(() => {
    if (expiryTrackingDisabled) return [];
    return pantryItems.filter(item => item.daysLeft !== null && item.daysLeft <= 3 && item.daysLeft >= 0);
  }, [pantryItems, expiryTrackingDisabled]);

  // Fetch AI-generated recipe suggestions with smart caching
  const fetchAISuggestions = useCallback(async (expiringItems) => {
    if (expiringItems.length === 0) {
      setAiSuggestions([]);
      setSuggestionsError(false);
      suggestionsRef.current = [];
      return;
    }

    // Create a hash of the expiring items to prevent unnecessary API calls
    // Only includes items that actually affect recipe suggestions (name, quantity, daysLeft)
    const itemsHash = expiringItems
      .map(item => `${item.name.toLowerCase()}-${item.quantity}-${item.daysLeft}`)
      .sort() // Sort to ensure consistent hash regardless of order
      .join('|');
    
    // Don't fetch if we already have suggestions for the same items or if already fetching
    if (itemsHash === lastExpiringItemsHashRef.current || isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setLoadingSuggestions(true);
    setSuggestionsError(false);
    
    try {
      const response = await apiService.getRecipeSuggestions(expiringItems);
      const suggestions = response.suggestions || [];
      // const isFallback = response.fallback || false; // Unused for now
      
      // If AI suggestions are empty or invalid, fallback to static suggestions
      if (suggestions.length === 0) {
        const staticSuggestions = getStaticSuggestions(expiringItems);
        setAiSuggestions(staticSuggestions);
        suggestionsRef.current = staticSuggestions;
      } else {
        setAiSuggestions(suggestions);
        suggestionsRef.current = suggestions;
      }
      lastExpiringItemsHashRef.current = itemsHash;
    } catch (error) {
      console.error('[AI CACHE] Failed to fetch AI suggestions:', error);
      
      // Fallback to static suggestions if AI fails
      const staticSuggestions = getStaticSuggestions(expiringItems);
      setAiSuggestions(staticSuggestions);
      setSuggestionsError(true);
      lastExpiringItemsHashRef.current = itemsHash;
      suggestionsRef.current = staticSuggestions;
    } finally {
      setLoadingSuggestions(false);
      isFetchingRef.current = false;
    }
  }, []); // No dependencies to prevent infinite loops

  // Load pantry data on component mount
  useEffect(() => {
    if (user) {
      loadPantryData(false); // Don't clear cache on initial load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Intentionally missing loadPantryData to prevent infinite loops

  // Fetch AI suggestions when expiring items change (with proper error handling)
  useEffect(() => {
    if (expiringSoon.length > 0) {
      // Debounce the API call to prevent excessive requests
      const timeoutId = setTimeout(() => {
        fetchAISuggestions(expiringSoon);
      }, 3000); // Increased delay to 3 seconds for better performance

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      setAiSuggestions([]);
      setSuggestionsError(false);
      suggestionsRef.current = [];
    }
  }, [expiringSoon, fetchAISuggestions]);

  useEffect(() => {
    if ((showAddForm || editingItem) && expiryTrackingDisabled) {
      setFormData(prev => ({ ...prev, expiryDate: getFallbackExpiryDate() }));
    }
  }, [showAddForm, editingItem, expiryTrackingDisabled, getFallbackExpiryDate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenQuickStart = params.get('quickstart') === '1';
    const quickStartDone = localStorage.getItem(quickStartKey) === 'true';
    if (shouldOpenQuickStart && !quickStartDone) {
      setShowQuickIngredientAdd(true);
      setIsQuickStartFlow(true);
    }
  }, [location.search, quickStartKey]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const itemData = {
        ...formData,
        expiryDate: expiryTrackingDisabled ? getFallbackExpiryDate() : (formData.expiryDate || null)
      };

      // The date input gives us YYYY-MM-DD format, which is already correct for backend
      const response = await apiService.addPantryItem(itemData);
      
      // Handle achievement notifications
      handleApiResponse(response);
      
      await loadPantryData(true); // Clear cache when items are added
      
      // Show success message with points if earned
      if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Item added successfully! +${response.pointsEarned} points earned!`);
      } else {
        showSuccess('Item added successfully! (Points already earned for pantry updates today)');
      }
      
      setShowAddForm(false);
      setFormData({
        name: '',
        quantity: '',
        location: 'pantry',
        expiryDate: getInitialExpiryDate(),
        description: ''
      });
    } catch (error) {
      console.error('Failed to add item:', error);
      showSuccess('Failed to add item. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const itemData = {
        ...formData,
        expiryDate: expiryTrackingDisabled ? getFallbackExpiryDate() : (formData.expiryDate || null)
      };

      // The date input gives us YYYY-MM-DD format, which is already correct for backend
      await apiService.updatePantryItem(editingItem.id, itemData);
      await loadPantryData(true); // Clear cache when items are updated
      
      // Award points for updating pantry item
      try {
        const activityResponse = await apiService.logActivity({
          activityType: 'pantry_updated',
          description: 'Updated pantry item',
          pointsEarned: 3
        });
        
        // Handle achievement notifications and stats refresh
        if (activityResponse && handleApiResponse) {
          handleApiResponse(activityResponse);
        }
        
        if (activityResponse?.pointsAwarded && activityResponse?.finalPoints > 0) {
          showSuccess(`Item updated successfully! +${activityResponse.finalPoints} points earned!`);
        } else {
          showSuccess('Item updated successfully! (No extra points available today)');
        }
      } catch (pointsError) {
        console.error('Failed to log points:', pointsError);
        showSuccess('Item updated successfully!');
      }
      
      setEditingItem(null);
      setFormData({
        name: '',
        quantity: '',
        location: 'pantry',
        expiryDate: getInitialExpiryDate(),
        description: ''
      });
    } catch (error) {
      console.error('Failed to update item:', error);
      showSuccess('Failed to update item. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setDeleteContext('manual');
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    setIsLoading(true);
    try {
      await apiService.deletePantryItem(itemToDelete.id);
      await loadPantryData(true); // Clear cache when items are deleted

      if (deleteContext === 'quantityZero') {
        showSuccess('Item removed from pantry.');
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        setDeleteContext('manual');
        return;
      }
      
      // Award points for deleting pantry item (cleanup action)
      try {
        const activityResponse = await apiService.logActivity({
          activityType: 'pantry_updated',
          description: 'Deleted pantry item',
          pointsEarned: 2
        });
        
        // Handle achievement notifications and stats refresh
        if (activityResponse && handleApiResponse) {
          handleApiResponse(activityResponse);
        }
        
        if (activityResponse?.pointsAwarded && activityResponse?.finalPoints > 0) {
          showSuccess(`Item deleted successfully! +${activityResponse.finalPoints} points earned!`);
        } else {
          showSuccess('Item deleted successfully! (No extra points available today)');
        }
      } catch (pointsError) {
        console.error('Failed to log points:', pointsError);
        showSuccess('Item deleted successfully!');
      }
      
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete item:', error);
      showSuccess('Failed to delete item. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteContext('manual');
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      quantity: item.quantity,
      location: item.location,
      expiryDate: expiryTrackingDisabled ? getFallbackExpiryDate() : (item.expiryDate || ''),
      description: item.description || ''
    });
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setShowAddForm(false);
    setFormData({
      name: '',
      quantity: '',
      location: 'pantry',
      expiryDate: getInitialExpiryDate(),
      description: ''
    });
  };

  const handleItemScannerClose = () => {
    setShowItemScanner(false);
  };

  const handleProductsExtracted = async (products) => {
    try {
      setIsLoading(true);
      let addedCount = 0;
      
      for (const product of products) {
      const itemData = {
        name: product.name,
        quantity: product.quantity || '1',
        location: product.location || 'pantry',
          expiryDate: expiryTrackingDisabled
            ? getFallbackExpiryDate()
          : (product.expiryDate || null),
        description: product.description || 'Added from scanner'
      };
        
        await apiService.addPantryItem(itemData);
        addedCount++;
      }
      
      if (addedCount > 0) {
        showSuccess(`Successfully added ${addedCount} item(s) to pantry!`);
        await loadPantryData(true); // Refresh the pantry items
      }
    } catch (error) {
      console.error('Failed to add items:', error);
      showSuccess('Failed to add some items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkSelectToggle = () => {
    setBulkSelectMode(!bulkSelectMode);
    setSelectedItems([]);
  };

  const handleItemSelect = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      // Deselect all if all are already selected
      setSelectedItems([]);
    } else {
      // Select all filtered items
      setSelectedItems(filteredItems.map(item => item.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    setIsLoading(true);
    try {
      // Use bulk delete API
      await apiService.bulkDeletePantryItems(selectedItems);
      
      await loadPantryData(true); // Clear cache when items are bulk deleted
      setBulkSelectMode(false);
      setSelectedItems([]);
      showSuccess(`${selectedItems.length} items deleted successfully!`);
    } catch (error) {
      console.error('Failed to delete items:', error);
      showSuccess('Failed to delete some items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quantity adjustment
  const handleQuantityAdjustment = async (itemId, adjustment) => {
    try {
      const item = pantryItems.find(i => i.id === itemId);
      if (!item) return;

      const parsedQuantity = parseQuantityText(item.quantity);
      if (parsedQuantity.value <= 0) {
        showSuccess('Cannot adjust quantity - invalid format');
        return;
      }

      // Keep a smaller step for decimal quantities (e.g. 0.5 kg), 1 for integer counts.
      const currentValue = parsedQuantity.value;
      const hasDecimal = !Number.isInteger(currentValue);
      const step = hasDecimal ? 0.5 : 1;
      const newNumber = currentValue + (adjustment * step);
      
      if (newNumber <= 0) {
        setItemToDelete(item);
        setDeleteContext('quantityZero');
        setShowDeleteConfirm(true);
        return;
      }

      const formattedNumber = formatQuantityValue(newNumber);

      // Format the new quantity
      let newQuantity;
      if (parsedQuantity.unit && parsedQuantity.unit.trim()) {
        // If there's a unit, preserve it
        newQuantity = `${formattedNumber} ${parsedQuantity.unit.trim()}`;
      } else {
        // If no unit, just use the number
        newQuantity = formattedNumber;
      }
      
      // Update the item
      const response = await apiService.updatePantryItem(itemId, {
        quantity: newQuantity
      });

      // Handle achievement notifications
      handleApiResponse(response);

      // Reload pantry data to reflect changes
      await loadPantryData(true); // Clear cache when quantities are updated
      
      showSuccess(`Quantity updated to ${newQuantity}`);
    } catch (error) {
      console.error('Failed to update quantity:', error);
      showSuccess('Failed to update quantity. Please try again.');
    }
  };

  const filteredItems = pantryItems.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const quickIngredientCategories = useMemo(() => ({
    Proteins: ['Chicken', 'Beef', 'Pork', 'Fish', 'Salmon', 'Tuna', 'Shrimp', 'Crab', 'Lobster', 'Turkey', 'Duck', 'Lamb', 'Bacon', 'Sausage', 'Ham', 'Eggs'],
    Dairy: ['Milk', 'Cheese', 'Cheddar', 'Mozzarella', 'Parmesan', 'Cream', 'Butter', 'Yogurt', 'Sour Cream', 'Cream Cheese'],
    Vegetables: ['Onions', 'Garlic', 'Tomatoes', 'Potatoes', 'Carrots', 'Broccoli', 'Spinach', 'Lettuce', 'Bell Peppers', 'Mushrooms', 'Zucchini', 'Eggplant', 'Cucumber', 'Celery', 'Cabbage', 'Cauliflower', 'Asparagus', 'Green Beans', 'Corn', 'Peas'],
    Fruits: ['Apples', 'Bananas', 'Oranges', 'Lemons', 'Limes', 'Berries', 'Strawberries', 'Blueberries', 'Raspberries', 'Grapes', 'Avocado', 'Pineapple', 'Mango', 'Peaches', 'Pears'],
    'Grains & Starches': ['Rice', 'Pasta', 'Bread', 'Flour', 'Oats', 'Quinoa', 'Barley', 'Couscous', 'Noodles', 'Tortillas', 'Crackers', 'Cereal'],
    'Pantry Staples': ['Olive Oil', 'Vegetable Oil', 'Salt', 'Pepper', 'Sugar', 'Brown Sugar', 'Honey', 'Vinegar', 'Soy Sauce', 'Worcestershire Sauce', 'Ketchup', 'Mustard', 'Mayonnaise', 'Hot Sauce', 'Sesame Oil', 'Coconut Oil'],
    'Spices & Herbs': ['Basil', 'Oregano', 'Thyme', 'Rosemary', 'Parsley', 'Cilantro', 'Dill', 'Chives', 'Cinnamon', 'Nutmeg', 'Ginger', 'Garlic Powder', 'Onion Powder', 'Paprika', 'Cumin', 'Coriander', 'Bay Leaves', 'Red Pepper Flakes', 'Black Pepper', 'Sea Salt'],
    'Canned & Jarred': ['Tomato Sauce', 'Tomato Paste', 'Coconut Milk', 'Chicken Broth', 'Beef Broth', 'Vegetable Broth', 'Beans', 'Chickpeas', 'Lentils', 'Olives', 'Pickles', 'Capers', 'Sun-dried Tomatoes'],
    'Nuts & Seeds': ['Almonds', 'Walnuts', 'Pecans', 'Cashews', 'Pistachios', 'Pine Nuts', 'Sesame Seeds', 'Chia Seeds', 'Flax Seeds', 'Peanuts', 'Peanut Butter', 'Almond Butter'],
    Baking: ['Baking Powder', 'Baking Soda', 'Vanilla Extract', 'Chocolate', 'Cocoa Powder', 'Powdered Sugar', 'Cornstarch', 'Yeast', 'Shortening']
  }), []);

  const quickIngredientCategoryOrder = useMemo(() => [
    'Proteins',
    'Dairy',
    'Vegetables',
    'Fruits',
    'Grains & Starches',
    'Pantry Staples',
    'Spices & Herbs',
    'Canned & Jarred',
    'Nuts & Seeds',
    'Baking'
  ], []);


  // Fallback static recipe suggestions
  const getStaticSuggestions = (expiringItems) => {
    const recipeMap = {
      'milk': ['Pancakes', 'Custard', 'Mac and Cheese'],
      'eggs': ['Scrambled Eggs', 'Omelette', 'French Toast'],
      'bread': ['French Toast', 'Bread Pudding', 'Croutons'],
      'chicken': ['Stir Fry', 'Chicken Salad', 'Soup'],
      'beef': ['Stir Fry', 'Beef Stew', 'Tacos'],
      'fish': ['Fish Tacos', 'Fish Curry', 'Grilled Fish'],
      'cheese': ['Grilled Cheese', 'Pasta', 'Quesadilla'],
      'tomatoes': ['Pasta Sauce', 'Bruschetta', 'Soup'],
      'onions': ['French Onion Soup', 'Stir Fry', 'Caramelized Onions'],
      'potatoes': ['Mashed Potatoes', 'Hash Browns', 'Soup'],
      'carrots': ['Carrot Cake', 'Soup', 'Stir Fry'],
      'apples': ['Apple Pie', 'Apple Crumble', 'Smoothie'],
      'bananas': ['Banana Bread', 'Smoothie', 'Pancakes'],
      'spinach': ['Salad', 'Smoothie', 'Pasta'],
      'lettuce': ['Salad', 'Wraps', 'Sandwich'],
      'broccoli': ['Stir Fry', 'Soup', 'Roasted'],
      'mushrooms': ['Stir Fry', 'Soup', 'Pasta'],
      'bell peppers': ['Stir Fry', 'Fajitas', 'Salad'],
      'zucchini': ['Zucchini Bread', 'Stir Fry', 'Pasta'],
      'cucumber': ['Salad', 'Tzatziki', 'Pickles']
    };

    const suggestions = new Set();
    expiringItems.forEach(item => {
      const itemName = item.name.toLowerCase();
      Object.keys(recipeMap).forEach(ingredient => {
        if (itemName.includes(ingredient)) {
          recipeMap[ingredient].forEach(recipe => suggestions.add(recipe));
        }
      });
    });

    return Array.from(suggestions).slice(0, 3);
  };

  // Get current recipe suggestions (AI or static fallback)
  const getRecipeSuggestions = () => {
    // If we have AI suggestions, use them
    if (aiSuggestions.length > 0) {
      return aiSuggestions.join(', ');
    }
    
    // Otherwise, use static suggestions as fallback
    const staticSuggestions = getStaticSuggestions(expiringSoon).join(', ');
    return staticSuggestions || 'recipe not found';
  };

  const toggleQuickIngredient = (ingredient) => {
    setSelectedQuickIngredients((prev) => (
      prev.includes(ingredient)
        ? prev.filter((item) => item !== ingredient)
        : [...prev, ingredient]
    ));
  };

  const handleAddQuickIngredients = async () => {
    if (selectedQuickIngredients.length === 0) {
      showSuccess('Select at least one ingredient.');
      return;
    }

    setIsLoading(true);
    try {
      const quickAddBatchId = `quickadd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const onboardingDone = localStorage.getItem(onboardingDoneKey) === 'true';
      const isOnboardingPhase = isQuickStartFlow || !onboardingDone;
      let addedCount = 0;
      let countForAchievementsUsed = false;

      for (const ingredient of selectedQuickIngredients) {
        try {
          await apiService.addPantryItem({
            name: ingredient,
            quantity: '1',
            location: 'pantry',
            expiryDate: expiryTrackingDisabled ? getFallbackExpiryDate() : null,
            description: 'Added from quick ingredient picker',
            isOnboardingFlow: isOnboardingPhase,
            countForAchievements: !countForAchievementsUsed,
            quickAddBatchId,
            quickAddSource: 'quick_picker'
          });
          addedCount++;
          if (!countForAchievementsUsed) {
            countForAchievementsUsed = true;
          }
        } catch (itemError) {
          console.error(`Failed to add quick ingredient: ${ingredient}`, itemError);
        }
      }

      if (addedCount > 0) {
        await loadPantryData(true);
      }

      if (addedCount === selectedQuickIngredients.length) {
        showSuccess(`Successfully added ${addedCount} item(s) to pantry!`);
      } else if (addedCount > 0) {
        showSuccess(`Added ${addedCount} item(s). Some items could not be added.`);
      } else {
        showSuccess('Failed to add selected items. Please try again.');
      }

      setSelectedQuickIngredients([]);
      setShowQuickIngredientAdd(false);
      if (isQuickStartFlow && addedCount > 0) {
        localStorage.setItem(quickStartKey, 'true');
        localStorage.setItem(onboardingReadyKey, 'true');
        setIsQuickStartFlow(false);
        navigate('/dashboard');
        window.dispatchEvent(new Event('startOnboardingTour'));
      }
    } catch (error) {
      console.error('Failed to add quick ingredients:', error);
      showSuccess('Failed to add selected items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const openManualAddForm = () => {
    setFormData({
      name: '',
      quantity: '',
      location: 'pantry',
      expiryDate: getInitialExpiryDate(),
      description: ''
    });
    setShowAddForm(true);
  };

  const handleSkipQuickStart = () => {
    localStorage.setItem(quickStartKey, 'true');
    localStorage.setItem(onboardingReadyKey, 'true');
    setSelectedQuickIngredients([]);
    setIsQuickStartFlow(false);
    setShowQuickIngredientAdd(false);
    navigate('/dashboard');
    window.dispatchEvent(new Event('startOnboardingTour'));
  };

  return (
    <div className={`pantry-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Header */}
      <div className="pantry-header">
        <div>
          <h1 className="pantry-title">
            <span>{'\uD83C\uDFE0'}</span>
            My Pantry
          </h1>
          <p className="pantry-subtitle">
            {expiryTrackingDisabled
              ? 'Manage your food inventory'
              : 'Manage your food inventory and track expiry dates'}
          </p>
        </div>
        <div className="header-actions">
          <button 
            className={`bulk-select-btn ${bulkSelectMode ? 'active' : ''}`}
            onClick={handleBulkSelectToggle}
            title="Select multiple items to delete"
          >
            <span>{bulkSelectMode ? '\u2611' : '\u2610'}</span>
            {bulkSelectMode ? 'Cancel Select' : 'Bulk Select'}
          </button>
          <button 
            className="scan-item-btn"
            onClick={() => {
              setShowItemScanner(true);
            }}
            title="Scan items, receipt, or barcode to add to pantry"
          >
            <span>{'\uD83D\uDCF7'}</span>
            Scan Item
          </button>
          <button
            className="quick-add-btn"
            onClick={() => setShowAddChoiceModal(true)}
            title="Choose manual add or quick add"
          >
            <span>+</span>
            Add Options
          </button>
        </div>
      </div>

 {/* Search Bar */}
 <div className="search-section">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search pantry items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="clear-search-btn"
              onClick={clearSearch}
              title="Clear search"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-number green">{stats.totalItems}</div>
          <div className="summary-label">Total Items</div>
        </div>
        {!expiryTrackingDisabled && (
          <>
            <div className="summary-card">
              <div className="summary-number orange">{stats.expiringSoon}</div>
              <div className="summary-label">Expiring Soon</div>
            </div>
            <div className="summary-card">
              <div className="summary-number red">{stats.expired}</div>
              <div className="summary-label">Expired</div>
            </div>
          </>
        )}
        <div className="summary-card">
          <div className="summary-number green">{stats.fridgeItems}</div>
          <div className="summary-label">In Fridge</div>
        </div>
      </div>

      {/* Alert Banner */}
      {!expiryTrackingDisabled && expiringSoon.length > 0 && (
        <div className="alert-banner">
          <span className="alert-icon">{'\u26A0\uFE0F'}</span>
          <div className="alert-content">
            <div className="alert-title">Items Expiring Soon!</div>
            <div className="alert-items">
              {expiringSoon.map(item => {
                const daysText = item.daysLeft === 0 ? 'today' : 
                                item.daysLeft === 1 ? 'tomorrow' : 
                                `in ${item.daysLeft} days`;
                return `${item.name} (${daysText})`;
              }).join(", ")}
            </div>
            <div className={`alert-suggestions ${loadingSuggestions ? 'loading' : ''} ${suggestionsError ? 'error' : ''}`}>
              <span className="suggestion-icon">{'\uD83D\uDCA1'}</span>
              <span>
                Try these recipes: {loadingSuggestions ? 'Loading...' : getRecipeSuggestions()}, for extra information talk to sous chef
              </span>
            </div>
          </div>
        </div>
      )}

     

      {/* Bulk Actions Bar */}
      {bulkSelectMode && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <span>{selectedItems.length} item(s) selected</span>
          </div>
          <div className="bulk-actions">
            <button 
              className="bulk-select-all-btn"
              onClick={handleSelectAll}
              disabled={isLoading || filteredItems.length === 0}
            >
              <span>{selectedItems.length === filteredItems.length && filteredItems.length > 0 ? '\u2611' : '\u2610'}</span>
              {selectedItems.length === filteredItems.length && filteredItems.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
            <button 
              className="bulk-delete-btn"
              onClick={handleBulkDelete}
              disabled={selectedItems.length === 0 || isLoading}
            >
              <span>{'\uD83D\uDDD1\uFE0F'}</span>
              Delete Selected ({selectedItems.length})
            </button>
          </div>
        </div>
      )}

      {/* Items Grid */}
      <div className="items-grid">
        {filteredItems.map((item) => (
          <div 
            key={item.id} 
            className={`item-card ${(!expiryTrackingDisabled && item.highlighted) ? 'highlighted' : ''} ${bulkSelectMode ? 'bulk-select-mode' : ''} ${selectedItems.includes(item.id) ? 'selected' : ''}`}
            onClick={bulkSelectMode ? () => handleItemSelect(item.id) : undefined}
            style={{ cursor: bulkSelectMode ? 'pointer' : 'default' }}
          >
            {bulkSelectMode && (
              <div className="bulk-select-checkbox">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item.id)}
                  onChange={() => handleItemSelect(item.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className="item-header">
              <div>
                <h3 className="item-title">{item.name}</h3>
                <div className="item-quantity-container">
                  <p className="item-quantity">{item.quantity}</p>
                  {!bulkSelectMode && /^\d+(?:\.\d+)?\s*/.test(item.quantity) && (
                    <div className="quantity-controls">
                      <button 
                        className="quantity-btn minus-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuantityAdjustment(item.id, -1);
                        }}
                        title="Decrease quantity"
                      >
                        {'\u2212'}
                      </button>
                      <button 
                        className="quantity-btn plus-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuantityAdjustment(item.id, 1);
                        }}
                        title="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {!bulkSelectMode && (
                <div className="item-actions">
                  <button 
                    className="action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(item);
                    }}
                    title="Edit item"
                  >
                    {'\u270F\uFE0F'}
                  </button>
                  <button 
                    className="action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteItem(item);
                    }}
                    title="Delete item"
                  >
                    {'\uD83D\uDDD1\uFE0F'}
                  </button>
                </div>
              )}
            </div>

            <div className="item-tags">
              <span className={`item-tag ${item.location}`}>
                {item.location === "fridge" ? "\uD83E\uDDCA" : item.location === "freezer" ? "\u2744\uFE0F" : item.location === "counter" ? "\uD83E\uDE9F" : item.location === "cabinet" ? "\uD83D\uDCC1" : "\uD83C\uDFE0"} {item.location}
              </span>
              {!expiryTrackingDisabled && item.daysLeft !== null && (
                <span className={`item-tag days ${item.daysLeft !== null && item.daysLeft <= 3 ? 'orange' : ''}`}>
                  {item.daysLeft < 0 ? "Expired" : 
                   item.daysLeft === 0 ? "Today" : 
                   `${item.daysLeft} days left`}
                </span>
              )}
            </div>

            {!expiryTrackingDisabled && item.daysLeft === null && (
              <div className="item-meta-note">! No expiry date</div>
            )}

            {!expiryTrackingDisabled && item.daysLeft !== null && item.daysLeft >= 0 && (
              <div className="progress-bar">
                <div 
                  className={`progress-fill ${item.daysLeft <= 3 ? 'orange' : ''}`}
                  style={{ width: `${Math.max(0, (item.daysLeft / 7) * 100)}%` }}
                ></div>
              </div>
            )}

            <p className="item-description">{item.description}</p>

            {!expiryTrackingDisabled && item.daysLeft !== null && item.daysLeft <= 3 && item.daysLeft >= 0 && (
              <div className="item-alert">
                <span>{'\u26A0\uFE0F'}</span>
                <span>Use soon!</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Item Modal - New Design */}
      {(showAddForm || editingItem) && (
        <div className="pantry-modal-overlay">
          <div className="pantry-modal-container">
            <div className="pantry-modal-header">
              <h2 className="pantry-modal-title">{editingItem ? 'EDIT PANTRY ITEM' : 'ADD NEW ITEM'}</h2>
              <div className="pantry-modal-subheader">
                <span className="pantry-kitchen">PANTRY MANAGEMENT</span>
                <HelpDisclosure title="Pantry item help" items={pantryItemHelp} />
              </div>
              <button className="pantry-modal-close" onClick={cancelEdit}>&times;</button>
            </div>
            
            <div className="pantry-modal-content">
              <form onSubmit={editingItem ? handleEditItem : handleAddItem}>
                <div className="form-group">
                <label htmlFor="name">Item Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="quantity">Quantity *</label>
                <input
                  type="text"
                  id="quantity"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  placeholder="e.g., 2 liters, 1 kg, 12 pieces"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="location">Location *</label>
                <select
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  required
                >
                  <option value="pantry">{'\uD83C\uDFE0'} Pantry</option>
                  <option value="fridge">{'\uD83E\uDDCA'} Fridge</option>
                  <option value="freezer">{'\u2744\uFE0F'} Freezer</option>
                  <option value="counter">{'\uD83E\uDE9F'} Counter</option>
                  <option value="cabinet">{'\uD83D\uDCC1'} Cabinet</option>
                </select>
              </div>
              
              {!expiryTrackingDisabled && (
                <div className="form-group">
                  <label htmlFor="expiryDate">Expiry Date</label>
                  <input
                    type="date"
                    id="expiryDate"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                  />
                  <button
                    type="button"
                    className="pantry-cancel-btn"
                    onClick={() => setFormData({...formData, expiryDate: ''})}
                  >
                    Clear Expiry Date
                  </button>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description..."
                  rows="3"
                />
              </div>
              
                <div className="pantry-modal-footer">
                  <button type="button" onClick={cancelEdit} className="pantry-cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="pantry-submit-btn" disabled={isLoading}>
                    {isLoading ? 'Saving...' : (editingItem ? 'Update Item' : 'Add Item')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAddChoiceModal && (
        <div className="pantry-add-choice-overlay" onClick={() => setShowAddChoiceModal(false)}>
          <div className="pantry-add-choice-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="pantry-add-choice-title">Choose Add Mode</h3>
            <p className="pantry-add-choice-subtitle">How do you want to add items?</p>
            <div className="pantry-add-choice-actions">
              <button
                className="pantry-add-choice-btn manual"
                onClick={() => {
                  setShowAddChoiceModal(false);
                  openManualAddForm();
                }}
              >
                Manual Add Item
              </button>
              <button
                className="pantry-add-choice-btn quick"
                onClick={() => {
                  setShowAddChoiceModal(false);
                  setSelectedQuickIngredients([]);
                  setShowQuickIngredientAdd(true);
                }}
              >
                Quick Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Ingredient Add Modal */}
      {showQuickIngredientAdd && (
        <div className="pantry-ingredient-modal-overlay">
          <div className="pantry-ingredient-modal-container">
            <div className="pantry-ingredient-modal-header">
              <h2 className="pantry-ingredient-modal-title">{isQuickStartFlow ? 'ADD TO PANTRY' : 'I HAVE...'}</h2>
              <div className="pantry-ingredient-modal-subheader">
                <span className="pantry-quick-kitchen">{isQuickStartFlow ? 'FIRST-TIME QUICK START' : 'QUICK PANTRY'}</span>
                <span className="modal-context-note">
                  {isQuickStartFlow ? 'Choose what you currently have at home, then continue to tutorial' : 'Select ingredients to add instantly'}
                </span>
              </div>
              <button
                className="pantry-ingredient-modal-close"
                onClick={() => {
                  if (isQuickStartFlow) {
                    handleSkipQuickStart();
                    return;
                  }
                  setShowQuickIngredientAdd(false);
                }}
              >
                x
              </button>
            </div>

            <div className="pantry-ingredient-modal-content">
              {isQuickStartFlow && (
                <div className="quickstart-info-box">
                  <h4>What to do now</h4>
                  <p>Pick ingredients you already have and press <strong>Add item(s)</strong>.</p>
                  <p>Pantry will track quantities and show expiry reminders. You can toggle expiry tracking anytime in Settings.</p>
                  <p>You can also press <strong>Skip for now</strong> and continue.</p>
                </div>
              )}
              {quickIngredientCategoryOrder.map((category) => (
                <div key={category} className="pantry-ingredient-category-section">
                  <h3 className="pantry-ingredient-category-title">{category}</h3>
                  <div className="pantry-ingredient-list">
                    {quickIngredientCategories[category].map((ingredient) => (
                      <label key={ingredient} className="pantry-ingredient-item">
                        <input
                          type="checkbox"
                          checked={selectedQuickIngredients.includes(ingredient)}
                          onChange={() => toggleQuickIngredient(ingredient)}
                        />
                        <span className="pantry-ingredient-name">{ingredient}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="pantry-ingredient-modal-footer">
              {isQuickStartFlow && (
                <button
                  className="pantry-ingredient-skip-btn"
                  onClick={handleSkipQuickStart}
                  disabled={isLoading}
                >
                  Skip for now
                </button>
              )}
              <button
                className="pantry-ingredient-clear-btn"
                onClick={() => setSelectedQuickIngredients([])}
                disabled={isLoading}
              >
                Clear All
              </button>
              <button
                className="pantry-ingredient-apply-btn"
                onClick={handleAddQuickIngredients}
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : `Add ${selectedQuickIngredients.length} Item(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && itemToDelete && (
        <ModalPortal>
        <div className="confirmation-overlay">
          <div className="delete-confirmation-card">
            <div className="delete-confirmation-header">
              <h3>{'\uD83D\uDDD1\uFE0F'} Delete Item</h3>
              <button className="confirmation-close" onClick={cancelDelete}>&times;</button>
            </div>
            
            <div className="delete-confirmation-body">
               <div className="delete-icon-large">{'\u26A0\uFE0F'}</div>
              <h4>
                {deleteContext === 'quantityZero'
                  ? `${itemToDelete.name} would reach 0. Remove this item from pantry?`
                  : 'Are you sure you want to delete this item?'}
              </h4>
              <div className="item-preview-box">
                <div className="preview-item-name">{itemToDelete.name}</div>
                <div className="preview-item-details">{itemToDelete.quantity} &bull; {itemToDelete.location}</div>
              </div>
              <p className="delete-warning-text">
                {deleteContext === 'quantityZero'
                  ? 'You can add it again later if needed.'
                  : 'This action cannot be undone.'}
              </p>
            </div>
            
            <div className="delete-confirmation-actions">
              <button 
                type="button" 
                onClick={cancelDelete} 
                className="cancel-btn"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDelete} 
                className="delete-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Removing...' : (deleteContext === 'quantityZero' ? 'Remove Item' : 'Delete Item')}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Achievement-Style Success Notification */}
      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">{'\u2705'}</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>&times;</button>
          </div>
        </div>
      )}

      {/* Unified Item Scanner Modal (includes Receipt, Groceries, and Barcode tabs) */}
      {showItemScanner && (
        <ItemScanner
          onClose={handleItemScannerClose}
          onProductsExtracted={handleProductsExtracted}
        />
      )}
    </div>
  );
};

export default Pantry;

